import React, { useMemo } from "react";
import { textToHtml } from "../../utils/htmlUtils";

/**
 * GridCell
 * A single <td> in the grid.
 *
 * Rendering model (see Grid.css for the full reasoning):
 *   - <td> is a pure positioning slot.  Carries sticky positioning,
 *     column width, row height, colspan/rowspan, and event handlers.
 *     Has NO border, NO background, NO padding.
 *   - <div class="simple-excel-cell"> is the actually-visible cell.
 *     Carries border, background, padding, content layout, and any
 *     per-cell styles from the Excel file.
 *
 * This split exists because position:sticky on a <td> fights with the
 * HTML table layout algorithm in ways that have no clean CSS fix.
 * Moving the visual layer to a regular block element sidesteps the
 * problem entirely.
 *
 * PERF: this component is React.memo'd and its parents pass STABLE
 * props (precomputed style objects, useCallback'd handlers). The event
 * closures are built here, below the memo boundary, so a selection drag
 * only re-renders the handful of cells whose className actually changed
 * instead of every cell in the sheet. The cell-content HTML is memoized
 * for the same reason.
 */
function GridCell({
  rowIndex, colIndex,
  cellValue, html, linkUrl,
  rowHeight, colWidth,
  effectiveColSpan, effectiveRowSpan,
  cellStyle,
  stickyStyle,
  className,
  filterButton,
  bumpZ,
  onStartSelection, onExtendSelection, onLinkClick, onCellContextMenu,
}) {
  const safeHtml = useMemo(
    () => html || textToHtml(cellValue == null ? "" : cellValue),
    [html, cellValue],
  );
  const titleText = linkUrl
    ? (cellValue == null ? "" : String(cellValue)) + "\n" + linkUrl
    : (cellValue == null ? "" : String(cellValue));

  // The <td> itself only needs sticky positioning + dimensions.
  // The inner div is sized to fill the td (width 100%) and matches the
  // requested row height — this is what guarantees the cell stays at
  // the right height even when sticky engages, because the inner div
  // is a regular block whose height is honoured (unlike sticky td).
  const tdStyle = {
    width: colWidth + "px",
    minWidth: colWidth + "px",
    maxWidth: effectiveColSpan > 1 ? "none" : colWidth + "px",
    height: rowHeight + "px",
    ...(linkUrl ? { cursor: "pointer" } : {}),
    ...stickyStyle,
    // When this cell's filter popup is open, lift it above the other frozen
    // rows/cells so the dropdown is never covered by the frozen pane.
    ...(bumpZ ? { zIndex: 60 } : {}),
  };

  // Cell-level styling from the Excel file goes on the inner div now
  // (it's the element that has the background, border, and padding).
  // The td/sticky background, set by the caller, is hoisted up to here
  // too if the cell is frozen — otherwise the cleanCellStyle wins.
  // Merged cells (rowSpan > 1) must fill the WHOLE merged height so the
  // bottom border sits at the merged region's bottom edge and vertical
  // centring uses the full region — not just the master row.
  const isMergedTall = effectiveRowSpan > 1;
  const innerStyle = {
    height: isMergedTall ? "100%" : rowHeight + "px",
    minHeight: isMergedTall ? "100%" : rowHeight + "px",
    ...cellStyle,
  };
  // The visible cell is a flex container, so Excel's textAlign /
  // verticalAlign are ignored by the layout.  Translate them to
  // justify-content / align-items so centred (and right/top/bottom)
  // cells — merged or not — render exactly where Excel places them.
  if (cellStyle) {
    if (cellStyle.textAlign === "center") innerStyle.justifyContent = "center";
    else if (cellStyle.textAlign === "right") innerStyle.justifyContent = "flex-end";
    if (cellStyle.verticalAlign === "top") innerStyle.alignItems = "flex-start";
    else if (cellStyle.verticalAlign === "bottom") innerStyle.alignItems = "flex-end";
    else if (cellStyle.verticalAlign === "middle") innerStyle.alignItems = "center";
  }
  // If the caller provided a sticky background (for frozen cells),
  // use that as the visible background.
  if (stickyStyle && stickyStyle.backgroundColor) {
    innerStyle.background = stickyStyle.backgroundColor;
  }

  // A filter cell becomes a positioning context so the dropdown button and
  // its popup (absolutely positioned) anchor to THIS cell.  Frozen cells are
  // already position:sticky (inline) which wins over this class.
  const tdClassName = filterButton ? `${className} sev-filter-cell`.trim() : className;

  return (
    <td
      colSpan={effectiveColSpan}
      rowSpan={effectiveRowSpan}
      onMouseDown={(event) => {
        if (!linkUrl) onStartSelection(rowIndex, colIndex, event);
      }}
      onMouseEnter={() => onExtendSelection(rowIndex, colIndex)}
      onClick={linkUrl ? (e) => { e.preventDefault(); onLinkClick(linkUrl); } : undefined}
      onContextMenu={(e) => onCellContextMenu(e, rowIndex, colIndex)}
      className={tdClassName}
      style={tdStyle}
      title={titleText}
    >
      <div
        className={linkUrl ? "simple-excel-cell sev-link" : "simple-excel-cell"}
        style={innerStyle}
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
      {filterButton}
    </td>
  );
}

export default React.memo(GridCell);
